import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  NgZone,
  ViewChild,
  inject,
} from '@angular/core';
import { LayoutService } from '../../services/layout.service';
import { LocalStorageService } from '../../../shared/services/local-storage.service';
import { BaseComponent } from '../base/base.component';
import { CommonModule } from '@angular/common';
import { IsActiveMatchOptions, NavigationEnd, Router, RouterModule } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { NavigationService } from '../../services/navigation.service';
import { NavItem } from '../../configs/navigation.config';
import { AvatarComponent } from "../../../shared/components/avatar/avatar.component";
import { LogoComponent } from '../../../shared/components/logo/logo.component';
import { PendingsBadgeService } from '../../../features/pendings/services/pendings-badge.service';
import { PermissionService } from '../../services/permission.service';

declare var Menu: any;

declare var window: any;
declare var Helpers: any;

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
  imports: [CommonModule, RouterModule, LogoComponent],
})
export class SidebarComponent extends BaseComponent implements AfterViewInit {
  @ViewChild('asideElement', { static: true }) asideElement: ElementRef;

  public usuarioNome: string;
  public empresaNome: string;
  public empresaLogoMarca: string
  public organizacaoChevron: boolean;

  public menuItems: any[] = [];

  // Contador de assuntos da fila no item "Pendências" (lembrete, não alarme).
  public readonly pendingsBadge = inject(PendingsBadgeService);

  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);
  private readonly permissions = inject(PermissionService);

  // Filho do submenu fica ativo na própria tela, com qualquer filtro na URL (?status=…).
  public readonly childActiveOptions: IsActiveMatchOptions = {
    paths: 'exact', queryParams: 'ignored', fragment: 'ignored', matrixParams: 'ignored',
  };

  // Aberto/fechado escolhido na seta, por grupo; sem escolha, o grupo abre quando a tela é dele.
  // Navegar zera as escolhas: entrar numa tela do grupo sempre mostra os filhos.
  private readonly groupToggles = new Map<string, boolean>();

  public isGroupOpen(item: NavItem): boolean {
    return this.groupToggles.get(item.url) ?? this.inGroup(item);
  }

  /**
   * Clique no pai do grupo. Aberto → fecha (fica na tela). Fechado → abre e, fora do grupo, vai
   * pra tela principal dele. Clique do meio/Ctrl/Shift segue o href (nova aba/janela).
   */
  public onGroupClick(item: NavItem, event: MouseEvent): void {
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey)
      return;
    event.preventDefault();

    if (this.isGroupOpen(item)) {
      this.groupToggles.set(item.url, false);
      return;
    }
    this.groupToggles.set(item.url, true);
    if (!this.inGroup(item)) {
      this.router.navigateByUrl(item.url);
      this.closeMobileMenu();
    }
  }

  /** A tela atual é do grupo (a lista, o formulário ou uma tela do submenu). */
  public inGroup(item: NavItem): boolean {
    const path = this.router.url.split(/[?#]/)[0];
    return path === item.url || path.startsWith(item.url + '/');
  }

  /**
   * Ponto no ícone só com o menu RECOLHIDO de verdade (desktop, sem hover). No celular a gaveta
   * abre sempre expandida mesmo com o "recolhido" do desktop gravado — lá vai o número.
   */
  public get pendingsAsDot(): boolean {
    return this.layoutService.menuCollapsed && !this.layoutService.menuHover && window.innerWidth >= 1200;
  }

  constructor(
    public layoutService: LayoutService,
    public localStorageService: LocalStorageService,
    private navigationService: NavigationService
  ) {

    super();
    this.inicializar();
    // Redesenha quando a permissão efetiva chega ou muda (troca de empresa pode trocar de grupo).
    this.permissions.changes$
      .pipe(takeUntilDestroyed())
      .subscribe(() => (this.menuItems = this.navigationService.getVisibleSections()));
    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd), takeUntilDestroyed())
      .subscribe(() => this.groupToggles.clear());
  }


  ngAfterViewInit(): void {
    // Menu do tema (Sneat), rolagem, cliques e hover: tudo FORA da zona do Angular. Esses eventos
    // só mexem em classes do DOM; dentro da zona, cada um (inclusive os resize que o tema dispara
    // na animação de recolher) reverificava a página inteira, e a lista de anúncios travava.
    this.zone.runOutsideAngular(() => {
      const aside = this.asideElement.nativeElement as HTMLElement;
      aside.addEventListener('mouseenter', () => this.layoutService.menuEnter());
      aside.addEventListener('mouseleave', () => this.layoutService.menuLeave());
      setTimeout(() => this.setupThemeMenu(), 200);
    });
  }

  private setupThemeMenu(): void {
      let animate,
        isHorizontalLayout = false;
      var templateName = document.documentElement.getAttribute('data-template');

      if (document.getElementById('layout-menu')) {
        isHorizontalLayout = document
          .getElementById('layout-menu')
          .classList.contains('menu-horizontal');
      }

      let layoutMenuEl = document.querySelectorAll('#layout-menu');
      layoutMenuEl.forEach(function (element) {
        var menu = new Menu(element, {
          orientation: isHorizontalLayout ? 'horizontal' : 'vertical',
          closeChildren: isHorizontalLayout ? true : false,

          showDropdownOnHover: localStorage.getItem(
            'templateCustomizer-' + templateName + '--ShowDropdownOnHover'
          ) // If value(showDropdownOnHover) is set in local storage
            ? localStorage.getItem(
              'templateCustomizer-' + templateName + '--ShowDropdownOnHover'
            ) === 'true' // Use the local storage value
            : window.templateCustomizer !== undefined // If value is set in config.js
              ? window.templateCustomizer.settings.defaultShowDropdownOnHover // Use the config.js value
              : true,
        });

        // No celular o PerfectScrollbar do tema intercepta o touchmove (preventDefault) e o
        // scroll JS dele não é confiável — itens do fim ficavam inalcançáveis. O próprio Sneat
        // resolve isso no manageScroll() (destrói o PS e liga overflow-auto nativo abaixo do
        // breakpoint), só que ele é chamado apenas no resize — nunca no load. Chamar no init
        // aplica o comportamento certo já na primeira pintura (desktop segue com PS).
        menu.manageScroll();

        window.Helpers.scrollToActive((animate = false));
        window.Helpers.mainMenu = menu;
      });

      let menuToggler = document.querySelectorAll('.layout-menu-toggle');
      menuToggler.forEach((item) => {
        item.addEventListener('click', (event) => {
          event.preventDefault();
          window.Helpers.toggleCollapsed();
          if (true && !window.Helpers.isSmallScreen()) {
            try {
              localStorage.setItem(
                'templateCustomizer-' + templateName + '--LayoutCollapsed',
                String(window.Helpers.isCollapsed())
              );
              let layoutCollapsedCustomizerOptions = document.querySelector(
                '.template-customizer-layouts-options'
              );
              if (layoutCollapsedCustomizerOptions) {
                let layoutCollapsedVal = window.Helpers.isCollapsed() ? 'collapsed' : 'expanded';
                (
                  layoutCollapsedCustomizerOptions.querySelector(
                    `input[value="${layoutCollapsedVal}"]`
                  ) as any
                ).click();
              }
            } catch (e) { }
          }
        });
      });
      let delay = (elem: any, callback: any) => {
        let timeout = null as any;
        elem.onmouseenter = function () {
          if (!Helpers.isSmallScreen()) {
            timeout = setTimeout(callback, 300);
          } else {
            timeout = setTimeout(callback, 0);
          }
        };

        elem.onmouseleave = function () {
          document.querySelector('.layout-menu-toggle').classList.remove('d-block');
          clearTimeout(timeout);
        };
      };
      if (document.getElementById('layout-menu')) {
        delay(document.getElementById('layout-menu'), function () {
          if (!Helpers.isSmallScreen()) {
            document.querySelector('.layout-menu-toggle').classList.add('d-block');
          }
        });
      }
  }

  // Fecha o drawer no mobile ao navegar por um item. No desktop a classe nem existe — no-op.
  public closeMobileMenu(): void {
    document.documentElement.classList.remove('layout-menu-expanded');
  }

  private inicializar() {
    const user = this.localStorageService.getSession();
    this.usuarioNome = user?.name || '';
  }

  public sair() {
    this.localStorageService.logout();
  }

  @HostListener('document:click', ['$event'])
  public onDocumentClick(event: MouseEvent): void {
    // Cliques no tour guiado (popover/overlay) não fecham o drawer — o tour abre o menu de
    // propósito pra apresentá-lo e controla o fechamento sozinho.
    if ((event.target as HTMLElement)?.closest?.('app-tour-overlay')) return;

    const clickedInside = this.asideElement.nativeElement.contains(event.target);
    if (!clickedInside) {
      document.querySelector('html').classList.remove('layout-menu-expanded');
    }
  }
}
